import type {
  RegionalHighlightPurchaseDurationMonths,
  RegionalHighlightPurchaseOption,
  RegionalHighlightPurchaseOptionsResponse,
} from '@/types/regionalHighlight';

type RecordValue = Record<string, unknown>;

const DURATION_MONTHS: RegionalHighlightPurchaseDurationMonths[] = [3, 6, 12];

const asRecord = (value: unknown): RecordValue | undefined =>
  typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as RecordValue)
    : undefined;

const asString = (value: unknown): string | undefined =>
  typeof value === 'string' ? value : undefined;

const asFiniteNumber = (value: unknown): number | undefined =>
  typeof value === 'number' && Number.isFinite(value) ? value : undefined;

const normalizeDistrictNames = (value: unknown): Record<string, string> | undefined => {
  const names = asRecord(value);
  if (!names) return undefined;

  return Object.entries(names).reduce<Record<string, string>>((result, [locale, label]) => {
    if (typeof label === 'string') result[locale] = label;
    return result;
  }, {});
};

const durationKey = (value: string): RegionalHighlightPurchaseDurationMonths | undefined => {
  const months = Number(value);
  return DURATION_MONTHS.includes(months as RegionalHighlightPurchaseDurationMonths)
    ? (months as RegionalHighlightPurchaseDurationMonths)
    : undefined;
};

const normalizeDurationNumberMap = (
  value: unknown,
): Partial<Record<RegionalHighlightPurchaseDurationMonths, number>> => {
  const raw = asRecord(value);
  if (!raw) return {};

  return Object.entries(raw).reduce<
    Partial<Record<RegionalHighlightPurchaseDurationMonths, number>>
  >((result, [key, entry]) => {
    const months = durationKey(key);
    const amount = asFiniteNumber(entry);
    if (months !== undefined && amount !== undefined) result[months] = amount;
    return result;
  }, {});
};

const normalizeDurationStringMap = (
  value: unknown,
): Partial<Record<RegionalHighlightPurchaseDurationMonths, string>> => {
  const raw = asRecord(value);
  if (!raw) return {};

  return Object.entries(raw).reduce<
    Partial<Record<RegionalHighlightPurchaseDurationMonths, string>>
  >((result, [key, entry]) => {
    const months = durationKey(key);
    const code = asString(entry);
    if (months !== undefined && code) result[months] = code;
    return result;
  }, {});
};

const normalizeOption = (value: unknown): RegionalHighlightPurchaseOption | undefined => {
  const raw = asRecord(value);
  const districtId = asString(raw?.districtId);
  const districtLabel = asString(raw?.districtLabel) ?? districtId;
  const positionsTotal = asFiniteNumber(raw?.positionsTotal);
  const positionsSold = asFiniteNumber(raw?.positionsSold);
  const positionsFree = asFiniteNumber(raw?.positionsFree);
  const searchesPerMonth = asFiniteNumber(raw?.searchesPerMonth);

  if (
    !raw ||
    !districtId ||
    !districtLabel ||
    positionsTotal === undefined ||
    positionsSold === undefined ||
    positionsFree === undefined ||
    searchesPerMonth === undefined ||
    typeof raw.purchasable !== 'boolean'
  ) {
    return undefined;
  }

  return {
    districtId,
    districtLabel,
    districtName: normalizeDistrictNames(raw.districtName),
    positionsTotal,
    positionsSold,
    positionsFree,
    monthlyPriceByDuration: normalizeDurationNumberMap(
      raw.monthlyPriceByDuration ?? raw.monthlyPriceChfCentsByDuration,
    ),
    productCodeByDuration: normalizeDurationStringMap(raw.productCodeByDuration),
    searchesPerMonth,
    purchasable: raw.purchasable,
    purchasableReason: asString(raw.purchasableReason ?? raw.reason),
    alreadyHoldsPosition:
      typeof raw.alreadyHoldsPosition === 'boolean' ? raw.alreadyHoldsPosition : undefined,
  };
};

const optionsFrom = (value: unknown): unknown[] => {
  if (Array.isArray(value)) return value;
  const raw = asRecord(value);
  if (raw && Array.isArray(raw.options)) return raw.options;
  if (raw && Array.isArray(raw.districts)) return raw.districts;
  return [];
};

export const normalizeRegionalHighlightPurchaseOptionsResponse = (
  value: unknown,
): RegionalHighlightPurchaseOptionsResponse => ({
  options: optionsFrom(value)
    .map(normalizeOption)
    .filter((option): option is RegionalHighlightPurchaseOption => Boolean(option)),
});
